import { Link } from "react-router-dom";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Container from "@/components/base/Container";
import ContactCards from "@/components/feature/ContactCards";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import useSeo from "@/hooks/useSeo";
import { quickStats } from "@/mocks/trending";
import { siteInfo } from "@/mocks/site";

const BASE = "https://iosfxw.cn";

const ABOUT_IMAGE =
  "https://readdy.ai/api/search-image?query=Editorial%20illustration%20of%20an%20abstract%20writing%20desk%20with%20stacked%20newspaper%20shapes%2C%20notebook%20blocks%20and%20timeline%20lines%2C%20warm%20cream%20paper%20background%2C%20vermillion%20and%20amber%20geometric%20shapes%2C%20deep%20teal%20accents%2C%20subtle%20grain%20texture%2C%20no%20text%2C%20no%20people%2C%20minimal%20flat%20vector%20style&width=1200&height=900&seq=rr-about-desk&orientation=landscape";

const principles = [
  {
    icon: "ri-check-double-line",
    title: "只整理公开信息",
    desc: "所有内容都有可查的公开出处与大致时间点。不采用无法核对的转述，也不把传闻写成结论。",
  },
  {
    icon: "ri-user-forbid-line",
    title: "不针对具体个人作事实认定",
    desc: "讨论停留在事件、行业与现象层面，避免复述指向个人的未经证实指控，这是本站最基本的底线。",
  },
  {
    icon: "ri-time-line",
    title: "保留时间线",
    desc: "事件会变化，观点会反转。我们把内容放回时间线里，让你看到它是怎么发展、怎么被澄清的。",
  },
  {
    icon: "ri-edit-2-line",
    title: "有错就改并注明",
    desc: "收到有效更正后会尽快修改，并在文末说明调整内容，不做无痕编辑。",
  },
  {
    icon: "ri-contrast-2-line",
    title: "区分事实与讨论",
    desc: "正文中区分「发生了什么」与「大家在讨论什么」，热度榜只表示相对活跃度，不表示事实成立。",
  },
];

const canDo = [
  "按时间顺序梳理假期前后被广泛讨论的事件",
  "解释行业惯例、定价逻辑与常见误解",
  "整理话题榜与分类入口，方便快速补齐内容",
  "对已发布的偏差内容做出更正并注明",
];

const cantDo = [
  "不发布未经核实的个人指控与私生活内容",
  "不提供任何形式的人身评价或站队结论",
  "不接受要求删除准确内容的商业诉求",
  "不保证所有话题都能覆盖，只做能力范围内的整理",
];

export default function About() {
  useSeo({
    title: "关于我们｜内容原则与更新节奏 - 瓜田速报",
    description:
      "了解瓜田速报的定位、内容原则与更新节奏。我们围绕五一假期整理公开热点事件与争议话题，坚持只做公开信息归纳，不针对具体个人作出事实认定。",
    keywords: "瓜田速报,关于我们,内容原则,假期事件盘点,五一热点复盘",
    canonical: `${BASE}/about`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田速报",
        url: `${BASE}/about`,
        inLanguage: "zh-CN",
        description: "瓜田速报的定位、内容原则与更新节奏说明。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "瓜田速报",
        url: BASE,
        email: siteInfo.email,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${BASE}/about` },
        ],
      },
    ],
  });

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[74px]">
        <section className="border-b border-background-200 bg-background-100 py-10 md:py-16">
          <Container>
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-8">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                  关于本站
                </span>
                <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[40px]">
                  关于瓜田速报
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {siteInfo.intro}
                </p>
              </div>
              <div className="lg:col-span-4">
                <dl className="grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  {quickStats.map((stat) => (
                    <div key={stat.label}>
                      <dd className="font-heading text-xl font-bold text-primary-700">
                        {stat.value}
                        <span className="ml-0.5 text-xs font-normal text-foreground-500">{stat.unit}</span>
                      </dd>
                      <dt className="mt-1 text-xs text-foreground-500">{stat.label}</dt>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <Container>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-14">
              <div className="lg:col-span-6">
                <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                  把假期里发生的事，整理成一条看得懂的线
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  假期的信息有一个特点：来得快、走得也快。几天不上线再回来，看到的往往是片段和结论，却不知道中间发生了什么。瓜田速报想解决的正是这个问题——我们把分散的公开讨论收集起来，按时间与主题重新排布，补上必要的背景说明。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  我们不追求「最快」，更在意「讲清楚」。一篇文章通常在事件有公开信息之后才开始整理，宁可晚一点，也不把不确定的东西写成确定的样子。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/topics"
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-book-open-line" />
                    浏览全部盘点
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-chat-3-line" />
                    联系我们
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-[360px]">
                  <img
                    src={ABOUT_IMAGE}
                    alt="瓜田速报的编辑与整理工作示意插画"
                    title="关于瓜田速报 - 假期事件盘点"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section id="principle" className="border-y border-background-200 bg-background-100 py-14 md:py-20">
          <Container>
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                <span className="h-1.5 w-1.5 rounded-full bg-secondary-500" />
                内容原则
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                五条写进流程里的规矩
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                这些原则不是口号，而是每篇文章发布前都会过一遍的检查项。它们决定了我们能写什么，也决定了我们不写什么。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100">
                    <i className={`${item.icon} text-lg text-secondary-700`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
              <div className="flex h-full flex-col justify-center rounded-lg border border-accent-200 bg-accent-100 p-5 md:p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-700" />
                <p className="mt-3 font-heading text-sm leading-relaxed text-foreground-800">
                  与其抢在所有人之前说点什么，不如等事情清楚之后，说点站得住的话。
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <Container>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
                <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100">
                    <i className="ri-check-line text-secondary-700" />
                  </span>
                  我们会做的
                </h2>
                <ul className="mt-5 space-y-3.5">
                  {canDo.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100">
                    <i className="ri-close-line text-primary-700" />
                  </span>
                  我们不会做的
                </h2>
                <ul className="mt-5 space-y-3.5">
                  {cantDo.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-indeterminate-circle-line mt-0.5 text-lg text-primary-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100 p-6 sm:grid-cols-3 md:p-7">
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">更新节奏</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{siteInfo.updateTime}，假期期间保持每日两次整理。</p>
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">收录范围</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  娱乐动态、影视综、品牌营销、出行消费与社交热点五个方向。
                </p>
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">更正流程</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  通过表单或邮件说明情况，核实后修改并在文末注明调整内容。
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="border-t border-background-200 bg-background-100 py-14 md:py-20">
          <Container>
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                想和我们说点什么
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                无论是内容更正、线索投稿还是转载沟通，都可以通过下面的方式找到我们。邮件与表单我们都会看，只是回复需要一点时间。
              </p>
            </div>
            <div className="mt-8 lg:max-w-4xl">
              <ContactCards />
            </div>
          </Container>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}